import React, { useState } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '@/context/AuthContext';
import { Label } from '@/components/ui/label';
import { Video, X, Loader2, Film, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const MAX_VIDEO_BYTES = 200 * 1024 * 1024; // 200 MB
const MAX_VIDEO_SECONDS = 180;             // 3 minutes
const MAX_VIDEOS = 3;
const ACCEPT = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
};

const formatMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const formatSec = (s) => {
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}:${r.toString().padStart(2, '0')}`;
};

/** Client-side duration check — rejects >3 min before we upload 200 MB. */
const probeDuration = (file) =>
  new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.src = URL.createObjectURL(file);
    el.onloadedmetadata = () => {
      const d = el.duration;
      URL.revokeObjectURL(el.src);
      resolve(Number.isFinite(d) ? d : null);
    };
    el.onerror = () => {
      URL.revokeObjectURL(el.src);
      resolve(null);
    };
  });

/**
 * VideoUploader — controlled component.
 *
 * Props:
 *  - value:   Array<{path,filename,size,content_type,uploaded_at}>
 *  - onChange(next):  parent stores the updated array
 */
export default function VideoUploader({ value = [], onChange }) {
  const { getAuthHeader } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const onDrop = async (accepted) => {
    if (!accepted.length) return;
    const remaining = MAX_VIDEOS - value.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_VIDEOS} videos per listing`);
      return;
    }
    const files = accepted.slice(0, remaining);
    if (accepted.length > remaining) {
      toast.warning(`Only ${remaining} added — you reached the ${MAX_VIDEOS}-video limit.`);
    }
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      if (file.size > MAX_VIDEO_BYTES) {
        toast.error(`${file.name}: ${formatMB(file.size)} exceeds the ${MAX_VIDEO_BYTES / (1024 * 1024)} MB limit`);
        continue;
      }
      const duration = await probeDuration(file);
      if (duration !== null && duration > MAX_VIDEO_SECONDS) {
        toast.error(`${file.name}: ${formatSec(duration)} exceeds the 3-minute limit`);
        continue;
      }

      const fd = new FormData();
      fd.append('file', file);
      try {
        setProgress(0);
        const res = await axios.post(`${API_URL}/videos/upload`, fd, {
          // NOTE: do NOT set Content-Type manually — axios needs to add the
          // multipart boundary itself. Setting `multipart/form-data` alone
          // (no boundary) makes some proxies/browsers reject the body with
          // no CORS header, which surfaces as `Network Error` in axios.
          headers: { ...getAuthHeader() },
          timeout: 600000, // 10 min upload budget for large files on slow connections
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        uploaded.push(res.data);
      } catch (err) {
        let msg;
        if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
          msg = 'Upload could not reach the server. This usually means the '
              + 'file is too large for your current hosting plan, or the '
              + 'connection dropped mid-upload. Try a shorter or smaller '
              + 'clip and confirm you have a stable internet connection.';
        } else if (err.code === 'ECONNABORTED') {
          msg = 'Upload timed out. Try a smaller file or a faster connection.';
        } else {
          const detail = err.response?.data?.detail;
          msg = typeof detail === 'string'
            ? detail
            : detail?.message || err.message || 'Upload failed';
        }
        toast.error(`${file.name}: ${msg}`);
      }
    }
    setUploading(false);
    setProgress(0);
    if (uploaded.length) {
      toast.success(`${uploaded.length} video(s) uploaded`);
      onChange([...value, ...uploaded]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    multiple: true,
    disabled: uploading,
  });

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div>
      <Label className="text-white font-medium mb-2 flex items-center gap-2">
        <Film className="w-4 h-4 text-[#F59E0B]" />
        Listing videos
        <span className="text-xs text-[#64748B] font-normal">
          (up to {MAX_VIDEOS} clips · 3 min · 200 MB each)
        </span>
      </Label>

      <div {...getRootProps()} data-testid="video-dropzone"
           className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
             isDragActive
               ? 'border-[#F59E0B] bg-[#F59E0B]/[0.06]'
               : 'border-[#1E293B] hover:border-[#F59E0B]/40'
           } ${uploading ? 'opacity-70 cursor-wait' : ''}`}>
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center text-[#94A3B8]">
            <Loader2 className="w-10 h-10 text-[#F59E0B] animate-spin mb-2" />
            <p className="text-sm">Uploading… {progress}%</p>
            <div className="w-full max-w-xs mt-2 h-1.5 bg-[#0B1120] rounded-full overflow-hidden">
              <div className="h-full bg-[#F59E0B] transition-all"
                   style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
            <p className="text-[#94A3B8] text-sm">
              {isDragActive ? 'Drop videos here…' : 'Drag & drop videos, or click to select'}
            </p>
            <p className="text-xs text-[#64748B] mt-1">MP4, MOV, WEBM up to 200 MB · 3 min</p>
          </>
        )}
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-4"
             data-testid="video-preview-grid">
          {value.map((v, idx) => (
            <div key={v.path}
                 className="relative group rounded-lg overflow-hidden border border-[#1E293B] bg-[#0B1120] p-3">
              <div className="flex items-start gap-2">
                <Video className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate" data-testid={`video-filename-${idx}`}>
                    {v.filename}
                  </p>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    {formatMB(v.size)} · {v.content_type?.split('/')[1] || 'video'}
                  </p>
                </div>
                <button type="button" onClick={() => remove(idx)}
                        data-testid={`video-remove-${idx}`}
                        className="text-[#94A3B8] hover:text-[#EF4444] transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
